/**
 * Safe in-app return URL helpers for post-login redirects (QUI-55).
 * Only same-origin relative paths are accepted — never open redirects.
 */

export const RETURN_URL_STORAGE_KEY = 'quilty_return_url';

export function pathnameFromReturnUrl(url: string): string {
  return url.split('?')[0]?.split('#')[0] ?? url;
}

/** True when `url` is a relative in-app path we may send the user to after auth. */
export function isSafeAppReturnUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  if (!url.startsWith('/')) return false;
  // Protocol-relative URLs (`//evil.example`) must never pass.
  if (url.startsWith('//')) return false;
  const pathname = pathnameFromReturnUrl(url);
  if (!pathname || pathname === '/login') return false;
  return true;
}

export function persistReturnUrl(url: string): void {
  if (typeof sessionStorage === 'undefined') return;
  if (!isSafeAppReturnUrl(url)) return;
  sessionStorage.setItem(RETURN_URL_STORAGE_KEY, url);
}

export function readPersistedReturnUrl(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  const url = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
  return isSafeAppReturnUrl(url) ? url : null;
}

export function clearPersistedReturnUrl(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
}

export function loginPathWithReturn(returnUrl: string): string {
  if (!isSafeAppReturnUrl(returnUrl)) return '/login';
  return `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function registerPathWithReturn(returnUrl: string): string {
  if (!isSafeAppReturnUrl(returnUrl)) return '/login?mode=register';
  return `/login?mode=register&returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** Current browser path including search + hash (empty string when SSR). */
export function currentLocationPath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname + window.location.search + window.location.hash;
}

/**
 * Resolve the best post-auth destination from (in order):
 * explicit intended path, sessionStorage, `?returnUrl=` on the current URL.
 */
export function resolvePostAuthReturnUrl(options?: {
  intendedPath?: string;
  queryReturnUrl?: string | null;
}): string | null {
  const intended = options?.intendedPath;
  if (intended && isSafeAppReturnUrl(intended) && pathnameFromReturnUrl(intended) !== '/') {
    return intended;
  }

  const stored = readPersistedReturnUrl();
  if (stored) return stored;

  const fromQuery = options?.queryReturnUrl;
  if (isSafeAppReturnUrl(fromQuery)) return fromQuery;

  return null;
}
